import React, { useMemo, useState } from 'react';
import {
  Table,
  Input,
  InputNumber,
  Popconfirm,
  Form,
  Switch,
  Button,
  Select,
} from 'antd';
import {
  onFilterFunction,
  UsageColumnComponents,
  UsageFilters,
  UsageTitle,
} from './AssetsTab/UsageColumnComponents';
import { IndexFund } from 'state';
import type { PaginationProps } from 'antd';

const { TextArea } = Input;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: 'number' | 'text' | 'boolean';
  record: unknown;
  index: number;
  children: React.ReactNode;
}

// const inputNodes: { [key: string]: JSX.Element } = {
//   number: <InputNumber />,
//   boolean: <Switch />,
// };

const inputNodes: { [key: string]: JSX.Element } = {
  number: <InputNumber style={{ width: '100%' }} />,
  boolean: <Switch />,
  text: (
    <TextArea
      autoSize={{ minRows: 2, maxRows: 8 }}
      style={{
        width: '100%',
        whiteSpace: 'pre-wrap', // wrap + preserve newlines
        overflowWrap: 'anywhere', // break long tokens/URLs
        wordBreak: 'break-word',
      }}
    />
  ),
  stringArray: (
    <Select
      mode="tags"
      tokenSeparators={[',', '\n']}
      style={{ width: '100%' }}
    />
  ),
};

const detectInputType = (record: any, dataIndex: string) => {
  const v = record?.[dataIndex];
  if (Array.isArray(v)) return 'stringArray';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  return 'text';
};

function normalizeArrayEntry(val: string | string[]): string[] {
  if (Array.isArray(val)) {
    return val.map(v => v.replace(/\\t/g, '\t'));
  }
  return []; // shouldn’t be called for plain strings
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) =>
  // {
  //   const inputNode = inputNodes[inputType] || <Input />;
  //
  //   return (
  //     <td {...restProps}>
  //       {editing ? (
  //         <Form.Item
  //           name={dataIndex}
  //           style={{ margin: 0 }}
  //           valuePropName={inputType === 'boolean' ? 'checked' : undefined}
  //           // rules={[
  //           //   {
  //           //     required: true,
  //           //     message: `Please Input ${title}!`,
  //           //   },
  //           // ]}
  //         >
  //           {inputNode}
  //         </Form.Item>
  //       ) : (
  //         children
  //       )}
  //     </td>
  //   );
  // };

  {
    const inputNode = inputNodes[inputType] || (
      <TextArea
        autoSize={{ minRows: 2, maxRows: 8 }}
        style={{
          width: '100%',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      />
    );

    return (
      <td
        {...restProps}
        style={{
          whiteSpace: 'normal', // allow wrapping in the cell
          wordBreak: 'break-word',
          ...restProps.style,
        }}
      >
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0, whiteSpace: 'normal' }}
            valuePropName={inputType === 'boolean' ? 'checked' : undefined}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

type Props<Item> = {
  data: Item[];
  onSave: (item: Item) => void;
  onDelete?: (item: Item) => void;
  fixedFields?: string[];
  ignoredFields?: string[];
  nonEditableFields?: string[];
  arrayFields?: string[];
  pagination?: PaginationProps;
  columns?: any;
  loading?: boolean;
  first_col_width?: string;
  buttons?: {
    onClick: (record: Item) => void;
    title: string;
    render?: (record: Item) => React.ReactNode;
  }[];
};

function EditableTable<Item extends { id: number }>({
  data,
  onSave,
  onDelete,
  fixedFields,
  ignoredFields,
  nonEditableFields,
  arrayFields,
  pagination,
  columns,
  loading,
  first_col_width,
  buttons,
}: React.PropsWithChildren<Props<Item>>) {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState(0);

  const cancel = () => {
    setEditingKey(0);
  };

  const tableColumns: {
    title: string;
    dataIndex: string;
    editable?: boolean;
    [key: string]: any;
  }[] = useMemo(() => {
    const isFixed = (key: string) => fixedFields?.includes(key);
    const isIgnored = (key: string) => ignoredFields?.includes(key);
    const isEditing = (record: Item) => record.id === editingKey;

    const edit = (record: Item) => {
      form.setFieldsValue({ ...record });
      setEditingKey(record.id);
    };

    const save = async (key: React.Key) => {
      try {
        const row = (await form.validateFields()) as any;
        const arrayFields_ = arrayFields || [];
        for (const field of arrayFields_) {
          if (field in row) {
            row[field] = normalizeArrayEntry(row[field]);
          }
        }

        onSave({ ...row, id: key } as Item);
        setEditingKey(0);
      } catch (errInfo) {
        console.log('Validate Failed:', errInfo);
      }
    };

    const formatColTitle = (key: string) => {
      const tokens = key.split('_');
      const upperTokens = tokens.map(
        t => `${t.charAt(0).toUpperCase()}${t.slice(1)}`
      );
      return upperTokens.join(' ');
    };

    const keyColumns = [
      {
        title: 'Operation',
        dataIndex: 'operation',
        fixed: 'left',
        width: `${first_col_width || '200px'}`,
        render: function Operation(value: never, record: Item) {
          const editable = isEditing(record);
          return editable ? (
            <span>
              <a onClick={() => save(record.id)} style={{ marginRight: 8 }}>
                Save
              </a>
              {onDelete && (
                <Popconfirm
                  title="Sure to delete?"
                  onConfirm={() => {
                    onDelete(record);
                  }}
                >
                  <a style={{ marginRight: 8 }}>Delete</a>
                </Popconfirm>
              )}
              <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                <a>Cancel</a>
              </Popconfirm>
            </span>
          ) : (
            <span>
              {/*<a style={{ marginRight: '10px' }} onClick={() => edit(record)}>*/}
              {/*  Edit*/}
              {/*</a>*/}
              {/*{buttons?.map((btn, i) => (*/}
              {/*  <Button*/}
              {/*    style={{*/}
              {/*      padding: '0 10px',*/}
              {/*      marginRight: i !== buttons?.length - 1 ? '10px' : '0',*/}
              {/*    }}*/}
              {/*    key={btn.title}*/}
              {/*    onClick={() => btn.onClick(record)}*/}
              {/*  >*/}
              {/*    {btn.title}*/}
              {/*  </Button>*/}
              {/*))}*/}
              <div style={{ display: 'flex', gap: '8px' }}>
                <a onClick={() => edit(record)}>Edit</a>
                {buttons?.map(btn =>
                  btn.render ? (
                    <div key={btn.title}>{btn.render(record)}</div>
                  ) : (
                    <Button
                      key={btn.title}
                      size="small"
                      onClick={() => btn.onClick(record)}
                    >
                      {btn.title}
                    </Button>
                  )
                )}
              </div>
            </span>
          );
        },
      },
      // {
      //   title: UsageTitle,
      //   dataIndex: 'usage',
      //   render: (_: any, record: IndexFund) => {
      //     return UsageColumnComponents(record, onClickUsage);
      //   },
      //   filters: UsageFilters,
      //   onFilter: (value, record) => {
      //     return onFilterFunction(value, record);
      //   },
      // },
      ...Object.keys(data[0] || {})
        .filter(key => !isIgnored(key))
        .sort((a, b) => (isFixed(a) && isFixed(b) ? 0 : isFixed(a) ? -1 : 1))
        .map((key, i, arr) => {
          const col = {
            key,
            title: formatColTitle(key),
            dataIndex: key,
            ...columns?.[key],
          };
          let fixed_ = isFixed(key) ? 'left' : undefined;
          if (i === arr.length - 1) {
            // last col
            fixed_ = 'right';
          }

          return {
            ...col,
            editable: (() => {
              nonEditableFields?.push('id');
              return !nonEditableFields?.includes(key);
            })(),
            // editable: key !== 'id',
            fixed: fixed_,
          };
        }),
    ];

    return keyColumns.map(col => {
      if (!col.editable) {
        return {
          ...col,
          render:
            col.render ??
            ((val: unknown) => {
              if (Array.isArray(val))
                return val.map(n => n.toLocaleString()).join(', ');
              return val;
            }),
        };
      }

      return {
        ...col,
        render:
          col.render ??
          ((val: unknown) => {
            if (typeof val === 'boolean')
              return <Switch disabled checked={val} />;
            if (Array.isArray(val)) return val.join(', ');
            // console.log(col);
            // console.log(val);
            // if (Array.isArray(val))
            //   return val.map(n => n.toLocaleString()).join(', ');

            return val;
          }),
        onCell: (record: Item) => ({
          record,
          // inputType: typeof (record as any)[col.dataIndex],
          inputType: detectInputType(record as any, col.dataIndex),
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });
  }, [
    buttons,
    columns,
    data,
    editingKey,
    fixedFields,
    form,
    ignoredFields,
    onSave,
  ]);
  // console.log(data);
  // console.log(tableColumns);

  if (!data) return null;

  return (
    <Form form={form} component={false}>
      <Table
        loading={loading}
        rowKey="id"
        // scroll={{ x: 'max-content' }}
        // scroll={{ x: '100%' }}
        // scroll={{ x: true }}
        scroll={{ y: '70vh', x: 'max-content' }} // Add horizontal scroll
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        // expandable={{
        //   expandedRowRender: function exp(record) {
        //     return <p style={{ margin: 0 }}>{(record as any).body}</p>;
        //   },
        //   rowExpandable: record => (record as any).body,
        // }}
        bordered
        dataSource={data}
        columns={tableColumns}
        tableLayout="fixed"
        rowClassName="editable-row"
        pagination={pagination || false}
      />
    </Form>
  );
}

export default EditableTable;
